"use client";

import { useAuth } from "@clerk/nextjs";
import { Icon } from "@to/ui";
import { TO_SETTINGS_PROFILE_URL } from "./nav-links";

/**
 * Auth affordance for the shell. Uses the Clerk hooks directly (NOT Clerk
 * wrapper components) — mirrors `apps/orgportal/.../VBCSaveButton.tsx` and
 * lesson 2026-04-15 (Clerk wrapper components throw under React 19).
 *
 * - Anonymous → "Agent Sign In" (white box, bordered, with a small "Powered by
 *   Trusted Only" line) linking to the Trusted Only platform sign-in — agents
 *   authenticate against Trusted Only, not an AD-local account.
 * - Signed in → "Settings" linking every role to the Trusted Only platform's
 *   profile settings. Sign-out lives inside the signed-in surfaces.
 *
 * Rendered only when Clerk is configured (the shell gates on `isClerkConfigured`),
 * so the hooks always run inside a `ClerkProvider`.
 */

/** Trusted Only platform sign-in — the one place agents log in. */
export const TO_SIGN_IN_URL = "https://app.trustedonly.com/en/sign-in";

/** Shared white-box face for both states (the v1 "sign out" style). */
const BOX_CLASS =
  "inline-flex items-center gap-2 rounded-lg border px-4 py-1.5 text-sm font-medium transition-colors";
const BOX_STYLE: React.CSSProperties = {
  borderColor: "var(--brand-card-border)",
  backgroundColor: "var(--brand-card-bg)",
  color: "var(--brand-text)",
};

export function AgentAuthButton() {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) {
    return (
      <button
        type="button"
        disabled
        className={`${BOX_CLASS} opacity-60`}
        style={BOX_STYLE}
        aria-label="Loading sign in"
      >
        <Icon name="progress_activity" size={18} className="animate-spin" />
        Agent Sign In
      </button>
    );
  }

  if (isSignedIn) {
    return (
      <a
        href={TO_SETTINGS_PROFILE_URL}
        className={BOX_CLASS}
        style={BOX_STYLE}
        aria-label="Settings — open your Trusted Only profile"
      >
        <Icon name="settings" size={18} />
        Settings
      </a>
    );
  }

  return <PublicAgentSignInButton />;
}

/** Customer hosts have no Clerk provider or session hooks. */
export function PublicAgentSignInButton() {
  return (
    <a
      href={TO_SIGN_IN_URL}
      className={BOX_CLASS}
      style={BOX_STYLE}
      aria-label="Agent Sign In — powered by Trusted Only"
    >
      <Icon name="login" size={18} />
      <span className="flex flex-col items-start leading-tight">
        <span>Agent Sign In</span>
        <span
          className="font-normal"
          style={{ color: "var(--brand-text-muted)", fontSize: "10px" }}
        >
          Powered by Trusted Only
        </span>
      </span>
    </a>
  );
}
